package org.example.project.registrierung

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object MobileFahrerRegistrierungApi {
    private const val API = "mobilefahrerregistrierungscreen"

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    data class LoginResult(val user: All? = null, val error: String = "")

    suspend fun login(email: String, password: String, mode: String = "mobile"): LoginResult {
        if (org.example.project.service.reloadForLatestLoginBuild()) return LoginResult(error = "build_reload")
        return runCatching {
            val response = window.fetch(
                "${LumaticaApiSettings.getBaseUrl()}/api/$API/login-json",
                RequestInit(
                    method = "POST",
                    headers = Headers().apply { append("Content-Type", "application/json") },
                    body = json.encodeToString(
                        buildJsonObject {
                            put("email", email.trim().lowercase())
                            put("password", password)
                            put("mode", mode)
                        }
                    )
                )
            ).await()
            val raw = response.text().await()
            if (response.ok) {
                LoginResult(user = decodeOne(raw), error = if (raw.isBlank() || raw == "null") "login_failed" else "")
            } else {
                val backendError = runCatching {
                    json.parseToJsonElement(raw) as? JsonObject
                }.getOrNull()?.get("error")?.jsonPrimitive?.content.orEmpty()
                LoginResult(error = backendError.ifBlank {
                    when (response.status.toInt()) {
                        429 -> "rate_limited"
                        in 500..599 -> "server_error"
                        else -> "login_failed"
                    }
                })
            }
        }.getOrElse { LoginResult(error = "network_error") }
    }

    suspend fun checkZweiFaktor(token: String, code: String, trustedDevice: Boolean): All? {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$API/zwei-faktor-pruefen",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(
                    buildJsonObject {
                        put("token", token)
                        put("code", code)
                        put("trustedDevice", trustedDevice)
                    }
                )
            )
        ).await()
        if (!response.ok) return null
        return decodeOne(response.text().await())
    }

    suspend fun changePassword(userId: String, firmenId: String, password: String, mode: String = "mobile"): All? =
        decodeOne(postPasswordJson("$API/password-change-json", userId, firmenId, password, mode))

    suspend fun saveSignatur(userId: String, firmenId: String, signatur: String): All? {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$API/signatur-save-json",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(
                    buildJsonObject {
                        put("userId", userId)
                        put("firmenId", firmenId)
                        put("signatur", signatur)
                    }
                )
            )
        ).await()
        if (!response.ok) return null
        return decodeOne(response.text().await())
    }

    suspend fun previewDriverSnapKey(snapKey: String): All? =
        decodeOne(
            LumaticaApiManager.call(
                listOf(API, "preview-driver-snapkey", snapKey).joinToString("/") { apiPart(it) }
            )
        )

    suspend fun registerDriver(fahrer: All, password: String, signatur: String): DriverRegistrationResponse {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$API/register-driver-json",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(
                    buildJsonObject {
                        put("password", password)
                        put("signatur", signatur)
                        put("fahrer", json.encodeToJsonElement(All.AllNames.serializer(), fahrer))
                    }
                )
            )
        )
            .await()
        if (!response.ok) return DriverRegistrationResponse()
        return decodeDriverRegistrationResponse(json, response.text().await())
    }

    private fun decodeOne(raw: String): All? =
        runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private suspend fun postPasswordJson(endpoint: String, first: String, firmenId: String, password: String, mode: String): String {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$endpoint",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(
                    buildJsonObject {
                        if (endpoint.endsWith("login-json")) {
                            put("email", first.trim().lowercase())
                            put("mode", mode)
                        } else {
                            put("userId", first)
                            put("firmenId", firmenId)
                            put("mode", mode)
                        }
                        put("password", password)
                    }
                )
            )
        ).await()
        return if (response.ok) response.text().await() else ""
    }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}
